import {HttpClient, HttpParams, HttpResponse, HttpStatusCode} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AdvertiserMaterial } from '@core/models/advertiser-material';
import { BackendAPI } from '@core/models/backend-api';
import { CalculateRequest } from '@core/models/calculate-request';
import { CancelOrderRequest } from '@core/models/cancel-order-request-model';
import { OrderHeader } from '@core/models/order-header.model';
import { OrderHistoryRecord } from '@core/models/order-history-record';
import { OrderUpdate } from '@core/models/order-update.model';
import { Order } from '@core/models/order.model';
import { SpaceAvailability } from '@core/models/space-availability';
import { SpaceAvailabilityRequest } from '@core/models/space-availability-request';
import { DateTimeService } from '@shared/services/date-time.service';
import {Observable, of, switchMap, timer} from 'rxjs';
import {map, repeat, skipWhile, take} from 'rxjs/operators';

export interface CreateOrUpdateOrderResponse {
	order: Order
	statusCode: HttpStatusCode
}
import {ReportType} from '@order/order/order.component';

@Injectable({
	providedIn: 'root'
})
export class OrdersService {

	constructor (
		private httpClient: HttpClient,
		private dateTimeService: DateTimeService) { }

	calculatePrice(calculateRequest: CalculateRequest): Observable<Order> {
		const url = BackendAPI.calculateOrderPrice;
		return this.httpClient.post<Order>(url, calculateRequest);
	}

	createOrUpdateOrder(orderUpdate: OrderUpdate): Observable<CreateOrUpdateOrderResponse> {
		// Call POST updateOrder. If status = 202, then response is a token to query with GET storeStatus (to check progress of store operation).
		// Poll GET storeStatus while it returns 202 and it will return the order.
		// Errors (e.g. 4xx, 5xx) are caught by the global interceptor.
		return this.httpClient.post(BackendAPI.updateOrder, orderUpdate, { responseType: 'text', observe: 'response' }).pipe(
			switchMap(updateOrderResponse => {
				if (updateOrderResponse.status === HttpStatusCode.Accepted) {
					return this.httpClient.get<Order>(`${BackendAPI.orderStoreStatus}/${updateOrderResponse.body}`, { observe: 'response' }).pipe(
						repeat({ delay: (count) => timer(Math.min(5000, (count - 5) * 500)) }),
						skipWhile(orderStoreStatus => orderStoreStatus.status === HttpStatusCode.Accepted),
						take(1),
						switchMap(orderStoreStatus => {
							return of({ order: orderStoreStatus.body, statusCode: orderStoreStatus.status } as CreateOrUpdateOrderResponse);
						})
					);
				} else {
					return of({ order: JSON.parse(updateOrderResponse.body!), statusCode: updateOrderResponse.status } as CreateOrUpdateOrderResponse);
				}
			})
		);
	}

	getOrderHeaders(bookedFromDate: Date, advertiserId: string | undefined, otherPayerId: string | undefined, pageOffset: number | undefined, pageSize: number | undefined, polySearch: string | undefined): Observable<OrderHeader[]> {

		// 17.1.5	GET /cass/orders/orderHeaders
		// Returns OrderHeader objects for orders associated with the user’s customer.
		// 17.1.5.1	Query Parameters
		// •	advertiserId: string
		// •	otherPayerId: string
		// •	bookedFromDate: string(format: YYYY-MM-DD)

		const url = BackendAPI.orderHeaders;
		const d = this.dateTimeService.toBackendDate(bookedFromDate);

		let requestParams = new HttpParams()
			.set("bookedFromDate", d);

		// both advertiserId and otherPayerId are now optional

		if (advertiserId) {
			requestParams = requestParams.set("advertiserId", advertiserId);
		}

		if (otherPayerId) {
			requestParams = requestParams.set("otherPayerId", otherPayerId);
		}

		if (pageOffset) {
			requestParams = requestParams.set("pageNo", pageOffset);
		}

		if (pageSize) {
			requestParams = requestParams.set("pageSize", pageSize);
		}

		if (polySearch) {
			requestParams = requestParams.set("polySearch", polySearch);
		}

		return this.httpClient.get<OrderHeader[]>(url, { params: requestParams });
	}

	getSpaceAvailability(request: SpaceAvailabilityRequest): Observable<SpaceAvailability> {
		const url = BackendAPI.orderSpaceAvailability;
		return this.httpClient.post<SpaceAvailability>(url, request);
	}

	getOrderById(id: string): Observable<Order> {
		return this.httpClient.get<Order>(BackendAPI.order + "/" + id);
	}

	getOrderHistoryById(id: string): Observable<OrderHistoryRecord[]> {
		return this.httpClient.get<OrderHistoryRecord[]>(BackendAPI.orderHistory + "/" + id);
	}

	exportToXls(basketId: string, reportType: ReportType) {
		return this.httpClient.get(BackendAPI.exportToXls + "?basketId=" + basketId + "&reportType=" + reportType, { responseType: 'blob' });
	}

	saveOrderMaterial(file: File, orderBasketId: string, materialId: string): Observable<any> {
		const url = BackendAPI.orderMaterial + `/${orderBasketId}/${materialId}`;
		const formData = new FormData();
		formData.append('file', file);
		return this.httpClient.put(url, formData, {
			reportProgress: true,
			observe: 'events'
		});
	}

	getMaterialPreview(orderId: string, materialId: string): Observable<Blob> {
		const url = BackendAPI.orderMaterial + `/${orderId}/${materialId}/preview`;
		return this.httpClient.get(url, {
			responseType: 'blob',
		});
	}

	getMaterialHires(orderId: string, materialId: string): Observable<Blob> {
		const url = BackendAPI.orderMaterial + `/${orderId}/${materialId}/pdfhires`;
		return this.httpClient.get(url, {
			responseType: 'blob',
		});
	}

	getMaterialForAdvertiser(advertiserId: string, width?: number, height?: number): Observable<AdvertiserMaterial[]> {
		let url: string = BackendAPI.orderMaterialForAdvertiser + `?advertiserId=${advertiserId}`;
		if (width) url += `&width=${width}`
		if (height) url += `&height=${height}`
		return this.httpClient.get<AdvertiserMaterial[]>(url);
	}

	updateOrderMaterialFromPreviousOrders(targetOrderBasketId: string, targetMaterialSeq: string, sourceOrderBasketId: string, sourceMaterialSeq: string): Observable<any> {
		const url = BackendAPI.orderMaterial + `/${targetOrderBasketId}/${targetMaterialSeq}/${sourceOrderBasketId}/${sourceMaterialSeq}`
		return this.httpClient.put(url, {})
	}

	cancelOrder(request: CancelOrderRequest): Observable<Order> {
		return this.createOrUpdateOrder(request).pipe(
			map((response: CreateOrUpdateOrderResponse) => {
				return response.order;
			})
		);
	}

	downloadCampaignReportPDF(orderBaskedId: string): Observable<Blob> {
		return this.httpClient.get(BackendAPI.orderConfirmationPDF + `?orderBasketId=${orderBaskedId}`, { responseType: 'blob' })
	}
}
